import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { WorkplaceAgreementPerUserDialogView } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementPerUserDialogView";
import { isEmpty } from "lodash";
import { useState } from "react";

/**
 * "per-user" variation: prompts each workplace user (not once per workplace) until they personally
 * accept, gating on `GET .../agreements?scope=user` with no first-shift delay.
 */
export function WorkplaceAgreementPerUserDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const isWorkplaceUser = isDefined(workplaceId) && !isAdmin && !isEmpty(userId);

  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, { scope: "user" }, { enabled: isWorkplaceUser });

  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId ?? "");

  const [visible, setVisible] = useState<boolean>(true);

  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId: workplaceId ?? "",
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!isWorkplaceUser) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  return (
    <WorkplaceAgreementPerUserDialogView
      isOpen={visible}
      isSubmitting={isLoadingCreateWorkplaceAgreement}
      onAgree={onAgree}
    />
  );
}
